import React, { useEffect, useState } from 'react';
import api from '../api';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../utils';
import { Skeleton } from './Skeleton';
import { useI18n } from '../context/I18nContext';

function ProgressBar({ pct }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const color =
    clamped >= 90 ? 'bg-accent-red'
      : clamped >= 70 ? 'bg-accent-yellow'
        : 'bg-accent-green';
  return (
    <div className="w-full h-3 bg-navy-900 border border-navy-600 rounded-full overflow-hidden">
      <div
        className={`h-3 ${color} transition-all duration-500 ease-out`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export default function UsageForecast() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get('/api/billing/usage/forecast')
      .then(res => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setError(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6" data-testid="usage-forecast-loading">
        <Skeleton height={16} className="w-48 mb-4" />
        <Skeleton height={12} className="w-full mb-2" />
        <Skeleton height={12} className="w-2/3" />
      </div>
    );
  }

  if (error || !data) return null;

  const {
    calls_this_month = 0,
    plan_limit,
    days_elapsed = 1,
    days_in_month = 30,
    projected_calls = 0,
    overage_estimate = 0,
    overage_cost_nok = 0,
    overage_rate_nok = 15,
    pct_used = 0,
  } = data;
  const planLimitLabel = plan_limit == null ? t('usage_forecast_unlimited', 'ubegrenset') : plan_limit;

  const daysRemaining = Math.max(0, days_in_month - days_elapsed);

  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6" data-testid="usage-forecast">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-accent-blue" />
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-500">{t('usage_forecast_title', 'Forbruksprognose')}</h3>
          </div>
          <h2 className="font-heading text-xl font-bold text-white">
            {calls_this_month} <span className="text-slate-500 font-normal text-base">{t('usage_forecast_of', 'av')} {planLimitLabel} {t('usage_forecast_calls_used', 'samtaler brukt')}</span>
          </h2>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('usage_forecast_day', 'Dag')}</div>
          <div className="text-lg font-heading font-bold text-white">{days_elapsed}<span className="text-sm text-slate-500 font-normal">/{days_in_month}</span></div>
        </div>
      </div>

      <ProgressBar pct={pct_used} />

      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-slate-500">{pct_used}% {t('usage_forecast_used_so_far', 'brukt så langt')}</span>
        <span className="text-slate-500">{daysRemaining} {t('usage_forecast_days_remaining', 'dager igjen av måneden')}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5 pt-5 border-t border-navy-600">
        <div className="bg-navy-900/50 border border-navy-700 rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{t('usage_forecast_eom_estimate', 'Estimat ved månedsslutt')}</div>
          <div className="text-lg font-heading font-bold text-white" data-testid="usage-forecast-projection">
            {projected_calls} <span className="text-sm text-slate-500 font-normal">{t('usage_forecast_calls_word', 'samtaler')}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {t('usage_forecast_avg_based_on', 'Basert på snitt på')} {(calls_this_month / Math.max(1, days_elapsed)).toFixed(1)} {t('usage_forecast_calls_per_day', 'samtaler/dag')}
          </div>
        </div>

        <div className={`border rounded-lg p-4 ${
          overage_estimate > 0
            ? 'bg-accent-red/10 border-accent-red/30'
            : 'bg-accent-green/10 border-accent-green/30'
        }`}>
          <div className="text-xs uppercase tracking-wider mb-1 text-slate-500">
            {t('usage_forecast_overage_label', 'Estimert overforbruk')}
          </div>
          {overage_estimate > 0 ? (
            <>
              <div className="text-lg font-heading font-bold text-accent-red flex items-center gap-2" data-testid="usage-forecast-overage">
                <AlertTriangle size={14} />
                +{overage_estimate} {t('usage_forecast_calls_word', 'samtaler')}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                ≈ {formatCurrency(overage_cost_nok)} ({overage_rate_nok} {t('usage_forecast_per_extra_call', 'kr per ekstra samtale')})
              </div>
            </>
          ) : (
            <>
              <div className="text-lg font-heading font-bold text-accent-green">
                {t('usage_forecast_within_plan', 'Innenfor planen')}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {t('usage_forecast_no_extra_costs', 'Ingen ekstra kostnader forventet denne måneden.')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
